import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import Card from "../ui/Card";
import Table from "../ui/Table";
import { fetchSupportTickets, addSupportReply } from "../utils/support";

export default function SupportMyTicketsPage() {
  const { user, activeOrgId } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [activeId, setActiveId] = useState("");
  const [reply, setReply] = useState("");
  const [statusMsg, setStatusMsg] = useState("");

  const orgId = activeOrgId || user?.orgId || "org_default";

  useEffect(() => {
    if (!user?.uid) return;
    fetchSupportTickets(user, orgId, { status: "all", mineOnly: true, max: 50 })
      .then((rows) => {
        setTickets(rows || []);
        if (rows?.length && !activeId) setActiveId(rows[0].id);
      })
      .catch(() => setTickets([]));
  }, [user, orgId, activeId]);

  const activeTicket = useMemo(
    () => tickets.find((t) => t.id === activeId) || null,
    [tickets, activeId]
  );

  const rows = tickets.map((t, i) => ([
    i + 1,
    t.subject || "Support request",
    t.category || "General",
    (t.status || "open").toUpperCase(),
    t.createdAt ? new Date(t.createdAt).toLocaleDateString() : "-",
  ]));

  const sendReply = async () => {
    if (!reply.trim() || !activeTicket) return;
    setStatusMsg("Sending reply...");
    const ok = await addSupportReply(user, orgId, activeTicket.id, reply.trim());
    if (ok) {
      setReply("");
      setStatusMsg("Reply sent.");
      fetchSupportTickets(user, orgId, { status: "all", mineOnly: true, max: 50 })
        .then((rows) => setTickets(rows || []))
        .catch(() => {});
    } else {
      setStatusMsg("Unable to send reply.");
    }
    setTimeout(() => setStatusMsg(""), 3500);
  };

  return (
    <div className="nia-page">
      <Card className="nia-hero">
        <div className="nia-header">
          <div>
            <h2>My Support Tickets</h2>
            <p>Track updates and replies to your support requests.</p>
          </div>
        </div>
      </Card>

      <div className="nia-grid nia-grid--2 support-grid">
        <Card title="My tickets">
          <Table
            headers={["#", "Subject", "Category", "Status", "Created"]}
            rows={rows.length ? rows : [["-", "-", "-", "-", "No tickets."]]}
            onRowClick={(rowIndex) => {
              const t = tickets[rowIndex];
              if (t?.id) setActiveId(t.id);
            }}
          />
        </Card>

        <Card title="Conversation">
          {!activeTicket ? (
            <div className="nia-muted">Select a ticket to view details.</div>
          ) : (
            <>
              <div className="nia-list" style={{ marginBottom: 12 }}>
                <div className="nia-list-btn">
                  <span>{activeTicket.subject}</span>
                  <span className="nia-pill">{activeTicket.status || "open"}</span>
                </div>
                <div className="nia-muted">
                  {activeTicket.category || "General"} •{" "}
                  {activeTicket.createdAt ? new Date(activeTicket.createdAt).toLocaleString() : "Just now"}
                </div>
              </div>

              <div className="nia-modal-body" style={{ maxHeight: 240, overflow: "auto" }}>
                {(activeTicket.messages || []).map((m) => (
                  <div key={m.id || m.createdAt} style={{ marginBottom: 10 }}>
                    <div style={{ fontWeight: 600 }}>{m.authorRole || "user"}</div>
                    <div className="nia-muted" style={{ fontSize: 12 }}>
                      {m.createdAt ? new Date(m.createdAt).toLocaleString() : "Just now"}
                    </div>
                    <div>{m.message}</div>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 12 }}>
                <label>Reply</label>
                <textarea
                  rows={3}
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Write a response…"
                />
                <div className="support-reply-actions">
                  <button className="btn btn-primary" onClick={sendReply}>Send reply</button>
                </div>
                {statusMsg && <div className="nia-muted" style={{ marginTop: 8 }}>{statusMsg}</div>}
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
